import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';

import styles from './Carousel.module.scss';

interface IButton {
  active: number;
  backgroundImage: string;
  index: number;
  onClick: () => void;
  setActive: React.Dispatch<React.SetStateAction<number>>;
  subtitle: ReactNode;
  title: ReactNode;
}

const Button = ({ active, backgroundImage, index, onClick = () => null, setActive, subtitle, title }: IButton) => {
  const activeClass: [string] | [] = active === index ? [styles.active] : [];
  const adjacentClass: [string] | [] = active === index - 1 ? [styles.adjacent] : [];
  const adjacentAdjacentClass: [string] | [] = active === index - 2 ? [styles['adjacent-adjacent']] : [];
  const className = [styles.item, ...activeClass, ...adjacentClass, ...adjacentAdjacentClass];
  return (
    <div className={className.join(' ')}>
      <button
        className={styles.button}
        onClick={(event: React.MouseEvent<HTMLElement>) => {
          setActive(index);
          const width = window.innerWidth >= 992 ? 300 : 150;
          if ((event.target as any).offsetWidth > width) {
            onClick();
          }
        }}
        onFocus={() => {
          setActive(index);
        }}
        style={{
          backgroundImage,
        }}
        type="button"
      >
        <span className={styles['button-text']}>
          <span className={styles['button-title']}>{title}</span>
          <span className={styles['button-subtitle']} role="presentation">
            {subtitle}
          </span>
        </span>
        <svg
          className={styles['button-icon']}
          xmlns="http://www.w3.org/2000/svg"
          width="33"
          height="32"
          viewBox="0 0 33 32"
          fill="none"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M16.4742 32.0062C25.3113 32.0062 32.4742 24.8433 32.4742 16.0062C32.4742 7.16911 25.3113 0.00622559 16.4742 0.00622559C7.63706 0.00622559 0.474182 7.16911 0.474182 16.0062C0.474182 24.8433 7.63706 32.0062 16.4742 32.0062ZM15.3313 9.14908V14.8634H9.61704V17.1491H15.3313V22.8634H17.617V17.1491H23.3313V14.8634H17.617V9.14908H15.3313Z"
            fill="white"
          />
        </svg>
      </button>
    </div>
  );
};

interface ICarouselItem {
  backgroundImage: string;
  subtitle: ReactNode;
  title: ReactNode;
  onClick: () => void;
}

interface ICarousel {
  items: ICarouselItem[];
}

export const Carousel = ({ items = [] }: ICarousel) => {
  const ref = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);

  const handler = useCallback(
    ({ code }: KeyboardEvent) => {
      if (code === 'ArrowLeft' && active > 0) {
        setActive(active - 1);
      }
      if (code === 'ArrowRight' && active < items.length - 1) {
        setActive(active + 1);
      }
    },
    [active, items.length],
  );

  useEffect(() => {
    window.addEventListener('keydown', handler);
    return () => {
      window.removeEventListener('keydown', handler);
    };
  }, [handler]);

  let startX = 0;

  const handleTouchStart = (event: React.TouchEvent<HTMLDivElement>) => {
    startX = event.touches[0].clientX;
  };

  const handleTouchEnd = (event: React.TouchEvent<HTMLDivElement>) => {
    const endX = event.changedTouches[0].clientX;
    const deltaX = endX - startX;

    if (deltaX > 50 && active > 0) {
      setActive(active - 1); // Swipe right
    } else if (deltaX < -50 && active < items.length - 1) {
      setActive(active + 1); // Swipe left
    }
  };

  return (
    <div
      className={styles.group}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      style={{ userSelect: 'none' }} // Disable text selection during swipe
      ref={ref}
    >
      {items.map(({ backgroundImage, onClick, subtitle, title }: ICarouselItem, index: number) => (
        <Button
          active={active}
          backgroundImage={backgroundImage}
          index={index}
          key={`${title}-${index}`}
          onClick={onClick}
          setActive={setActive}
          subtitle={subtitle}
          title={title}
        />
      ))}
    </div>
  );
};
